import './style.css';
import { Mesh, MeshBasicMaterial, SphereGeometry, Vector3 } from 'three';
import { AudioEngine } from './audio';
import { AbilityTracker } from './characters/AbilityTracker';
import { SEISMIC_SLAM_RADIUS } from './characters/abilities';
import { PassiveMeter } from './characters/passives';
import { resolveCharacterTuning } from './characters/resolveTuning';
import { CHARACTERS, resolveCharacter } from './characters/roster';
import type { CharacterDef } from './characters/types';
import { LevelCollider } from './collision/LevelCollider';
import { CombatWorld } from './combat/CombatWorld';
import { DuelistChampion } from './combat/DuelistChampion';
import { ProjectileVisuals } from './combat/ProjectileVisuals';
import { TargetDummies } from './combat/TargetDummies';
import { Engine } from './core/Engine';
import { Input } from './core/Input';
import { MouseLook } from './core/MouseLook';
import { effectiveFov } from './core/portraitFov';
import { loadSettings, saveSettings } from './core/settings';
import { detectTouchMode, TouchControls } from './core/TouchControls';
import { AbilityWorld } from './game/AbilityWorld';
import { MapTriggers } from './game/MapTriggers';
import { formatTicks, RunTimer } from './game/RunTimer';
import { loadRecords, submitRun } from './game/records';
import { loadGhost, type ReplayData, ReplayRecorder, sampleReplay, saveGhost } from './game/replay';
import { buildRunSummary } from './game/runSummary';
import { SlowMotion } from './game/SlowMotion';
import { SpeedFeel } from './game/SpeedFeel';
import { EYE_HEIGHT, PLAYER_HEIGHT, PLAYER_RADIUS } from './movement/constants';
import { FlyCamera } from './movement/FlyCamera';
import { PlayerController } from './movement/PlayerController';
import { tuning } from './movement/tuning';
import { installPageHook } from './testing/pageHook';
import { DebugHud } from './ui/DebugHud';
import { DamageNumbers } from './ui/damageNumbers';
import { Hud } from './ui/hud';
import { medalBadgeClass } from './ui/leaderboard';
import { buildSettingsPanel } from './ui/SettingsPanel';
import { StrafeSync } from './ui/strafeSync';
import { TuningPanel } from './ui/TuningPanel';
import { loadBspLevel } from './world/bsp/loadBspLevel';
import { addEnvironment } from './world/environment';
import { MAPS, type MapDef, resolveMap } from './world/maps';
import type { BuiltLevel } from './world/TestLevel';

const canvas = document.querySelector<HTMLCanvasElement>('#app');
const hudRoot = document.querySelector<HTMLElement>('#hud');
const overlay = document.querySelector<HTMLElement>('#overlay');
const mapList = document.querySelector<HTMLElement>('#map-list');
const heroList = document.querySelector<HTMLElement>('#hero-list');
if (!canvas || !hudRoot || !overlay || !mapList || !heroList) {
  throw new Error('Missing #app, #hud, #overlay, #map-list, or #hero-list in index.html');
}

const settings = loadSettings();
const engine = new Engine(canvas);
engine.camera.fov = settings.fov;
engine.camera.updateProjectionMatrix();
engine.setQuality(settings.quality);

const input = new Input(canvas);
// Touch mode (spec 0029): decided once at startup; on desktop nothing below
// constructs, so keyboard/pointer-lock behavior is untouched.
const touchMode = detectTouchMode();
if (touchMode) {
  document.body.classList.add('touch');
  const overlayHint = document.querySelector<HTMLElement>('#overlay p');
  if (overlayHint) {
    overlayHint.textContent = 'Tap anywhere to surf';
  }
}
const look = new MouseLook(input);
look.sensitivity = settings.sensitivity;
const fly = new FlyCamera(input, look);
const hud = new Hud(
  hudRoot,
  touchMode
    ? {
        onPress: (key) => input.setSynthetic(key, true),
        // Hold the synthetic key a few sim ticks past release: an ultra-quick
        // tap must still be seen by the 100 Hz edge detector.
        onRelease: (key) => window.setTimeout(() => input.setSynthetic(key, false), 60),
      }
    : undefined,
);
const touchControls = touchMode ? new TouchControls(input, { onMenu: endTouchSession }) : null;
let touchSessionEndedAt = 0;
function endTouchSession(): void {
  input.setTouchSession(false);
  touchControls?.hide();
  touchSessionEndedAt = performance.now();
  syncOverlay();
}
const debugHud = new DebugHud(document.body);
const tuningPanel = new TuningPanel(document.body, settings, look, engine.camera);
const sync = new StrafeSync();
const records = loadRecords();
const audio = new AudioEngine();
audio.setVolume(settings.volume);

// Active hero, its ability cooldowns, and any active timed buff. Set by
// applyCharacter(). `currentBaseTuning` is the hero's resolved feel; a timed
// effect layers absolute overrides on top and reverts to it on expiry.
const abilities = new AbilityTracker();
const effects = new AbilityTracker();
const abilityWorld = new AbilityWorld(engine.scene);

// Combat layer (specs 0025/0026): the deterministic sim + its presentation.
// Practice maps declare no dummies, so only the player registers and every
// damage payload is inert there.
const PLAYER_MAX_HEALTH = 600;
const combat = new CombatWorld();
const dummies = new TargetDummies(engine.scene, combat);
const projectileVisuals = new ProjectileVisuals(engine.scene, combat);
const damageNumbers = new DamageNumbers(document.body);
// The AI duelist (spec 0028) + the session-only duel score.
let duelist: DuelistChampion | null = null;
let duelKills = 0;
let duelDeaths = 0;
// Full-screen edge glow shown during Vaelith's Aether Apex flight overdrive.
const apexOverlay = document.createElement('div');
apexOverlay.id = 'apex-overlay';
document.body.appendChild(apexOverlay);
let currentCharacter: CharacterDef = resolveCharacter(settings.selectedCharacter);
let currentBaseTuning = resolveCharacterTuning(currentCharacter);
let activeEffectId: string | null = null;

// Passive resource (spec 0024): fills from the hero's playstyle each tick; a
// full meter surges — every running cooldown is hastened + a HUD/VFX flash.
const passiveMeter = new PassiveMeter();
let surgeFlashSec = 0;

// High-speed feel (spec 0024): a smoothed FOV kick + vignette above ~900 u/s.
// Fully disabled under prefers-reduced-motion (checked live each frame).
const speedFeel = new SpeedFeel();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** '#rrggbb' hero accent → the numeric color three.js VFX expect. */
function accentColor(accent: string): number {
  return Number.parseInt(accent.replace('#', ''), 16);
}

function clearActiveEffect(): void {
  if (activeEffectId) {
    Object.assign(tuning, currentBaseTuning);
    activeEffectId = null;
  }
  effects.reset();
}

const settingsBody = document.querySelector<HTMLElement>('#settings-body');
if (settingsBody) {
  buildSettingsPanel(
    settingsBody,
    settings,
    look,
    engine.camera,
    (v) => audio.setVolume(v),
    (q) => engine.setQuality(q),
  );
}

// --- Hero select -------------------------------------------------------------
// Heroes are a movement-feel layer over the surf core (ADR 0005): picking one
// re-weights the live `tuning` copy the controller already reads each tick — no
// physics change. The default Surfer resolves to TUNING_DEFAULTS, so the base
// feel is preserved bit-for-bit. Abilities/resources come in a later iteration.

// A panel under the row showing the SELECTED hero's blurb + full skill list, so
// you can read what each Q/E/V does without hovering.
const heroInfo = document.createElement('div');
heroInfo.className = 'hero-info';
heroList.after(heroInfo);

function renderHeroInfo(def: CharacterDef): void {
  heroInfo.replaceChildren();
  heroInfo.style.setProperty('--accent', def.accent);

  const blurb = document.createElement('p');
  blurb.className = 'hero-info-blurb';
  blurb.textContent = def.blurb;
  heroInfo.append(blurb);

  const list = document.createElement('ul');
  list.className = 'hero-info-skills';
  if (!def.abilities || def.abilities.length === 0) {
    const li = document.createElement('li');
    li.className = 'hero-info-none';
    li.textContent = 'No abilities — just the pure surf feel.';
    list.append(li);
  } else {
    for (const a of def.abilities) {
      const li = document.createElement('li');
      const key = document.createElement('span');
      key.className = 'hero-info-key';
      key.textContent = a.key.replace('Key', '');
      const icon = document.createElement('span');
      icon.className = 'hero-info-icon';
      icon.style.setProperty('--icon', `url("/assets/skills/${a.icon}.svg")`);
      const text = document.createElement('span');
      text.className = 'hero-info-text';
      const nameEl = document.createElement('b');
      nameEl.textContent = a.name;
      text.append(nameEl, document.createTextNode(` — ${a.description}`));
      li.append(key, icon, text);
      list.append(li);
    }
  }
  // Passive row (P) — the always-on resource identity, set apart from the kit.
  if (def.passive) {
    const li = document.createElement('li');
    li.className = 'hero-info-passive';
    const key = document.createElement('span');
    key.className = 'hero-info-key';
    key.textContent = 'P';
    const text = document.createElement('span');
    text.className = 'hero-info-text';
    const nameEl = document.createElement('b');
    nameEl.textContent = def.passive.name;
    text.append(nameEl, document.createTextNode(` — ${def.passive.description}`));
    li.append(key, text);
    list.append(li);
  }
  heroInfo.append(list);
}

function applyCharacter(def: CharacterDef): void {
  currentBaseTuning = resolveCharacterTuning(def);
  Object.assign(tuning, currentBaseTuning);
  currentCharacter = def;
  abilities.reset(); // fresh cooldowns on a hero switch
  passiveMeter.reset(); // resources are earned, never carried across a switch
  surgeFlashSec = 0;
  clearActiveEffect();
  settings.selectedCharacter = def.id;
  saveSettings(settings);
  hud.setHero(def.name, def.accent);
  renderHeroInfo(def);
  for (const button of heroList?.querySelectorAll('button') ?? []) {
    button.classList.toggle('active', button.dataset.heroId === def.id);
  }
}

for (const def of CHARACTERS) {
  const card = document.createElement('button');
  card.className = 'hero-card';
  card.dataset.heroId = def.id;
  card.style.setProperty('--accent', def.accent);

  const portrait = document.createElement('span');
  portrait.className = 'hero-portrait';
  portrait.style.setProperty('--portrait', `url("/assets/heroes/${def.portrait}.svg")`);

  const name = document.createElement('span');
  name.className = 'hero-name';
  name.textContent = def.name;

  const cls = document.createElement('span');
  cls.className = 'hero-class';
  cls.textContent = def.className;

  card.append(portrait, name, cls);

  // Show what the hero's kit does: a small row of its skill icons (Q/E/V). The
  // full descriptions live in the hero-info panel below (updated on select).
  if (def.abilities && def.abilities.length > 0) {
    const skills = document.createElement('span');
    skills.className = 'hero-skills';
    for (const ability of def.abilities) {
      const skill = document.createElement('span');
      skill.className = 'hero-skill';
      skill.style.setProperty('--icon', `url("/assets/skills/${ability.icon}.svg")`);
      skills.appendChild(skill);
    }
    card.append(skills);
  }
  card.addEventListener('click', (e) => {
    e.stopPropagation(); // don't trigger the overlay's pointer-lock click
    audio.start(); // user gesture — safe place to create the AudioContext
    audio.click();
    applyCharacter(def);
  });
  heroList.appendChild(card);
}

// Apply the saved hero (or the default) before the first tick.
applyCharacter(resolveCharacter(settings.selectedCharacter));

/** Refresh map-button medal badges from current PBs. */
function refreshMapBadges(): void {
  for (const button of mapList?.querySelectorAll('button') ?? []) {
    const def = MAPS.find((m) => m.id === button.dataset.mapId);
    if (!def) {
      continue;
    }
    const badge = button.querySelector('.map-badge');
    if (badge) {
      badge.className = `map-badge ${medalBadgeClass(records.maps[def.id]?.pbTicks, def)}`;
    }
  }
}
// Interacting with overlay sections must not grab pointer lock.
for (const details of document.querySelectorAll('#overlay details')) {
  details.addEventListener('click', (e) => e.stopPropagation());
}
// The editor link navigates away — don't let the overlay grab pointer lock first.
document.querySelector('#editor-link')?.addEventListener('click', (e) => e.stopPropagation());

// Import a Counter-Strike surf map (.bsp): parse it in the browser into a
// transient map and play it. Clicking the label/input must not grab pointer lock.
const bspInput = document.querySelector<HTMLInputElement>('#bsp-input');
const bspStatus = document.querySelector<HTMLElement>('#bsp-status');
document.querySelector('#bsp-label')?.addEventListener('click', (e) => e.stopPropagation());
bspInput?.addEventListener('change', () => {
  const file = bspInput.files?.[0];
  if (!file || loading) {
    return;
  }
  if (bspStatus) {
    bspStatus.textContent = `Loading ${file.name}…`;
  }
  void file
    .arrayBuffer()
    .then((buffer) => {
      const id = 'bsp_import';
      return switchMap({
        id,
        name: file.name.replace(/\.bsp$/i, ''),
        load: (scene) => loadBspLevel(id, buffer, scene),
      });
    })
    .then(() => {
      if (bspStatus) {
        bspStatus.textContent = `Loaded ${file.name} — click to surf`;
      }
    })
    .catch((err: unknown) => {
      console.error('BSP import failed', err);
      if (bspStatus) {
        bspStatus.textContent = `Could not load ${file.name}: ${(err as Error).message}`;
      }
    });
});

// --- Map lifecycle -----------------------------------------------------------

const LAST_MAP_KEY = 'fall-line.lastMap';
let level: BuiltLevel;
let currentDef: MapDef;
let collider: LevelCollider;
let player: PlayerController;
let timer: RunTimer;
let triggers: MapTriggers;
const slowmo = new SlowMotion();

// Ghost replay: record the live run, race the saved PB ghost (a translucent orb).
const recorder = new ReplayRecorder();
let ghost: ReplayData | null = null;
const ghostPos = new Vector3();
const ghostMesh = new Mesh(
  new SphereGeometry(30, 16, 12),
  new MeshBasicMaterial({ color: 0x7fdfff, transparent: true, opacity: 0.4, depthWrite: false }),
);
ghostMesh.renderOrder = 2;
ghostMesh.frustumCulled = false;
ghostMesh.visible = false;

let botActive = false;
let splitMessage = '';
let splitMessageTicks = 0;
let finishHandled = false;
let loading = true;

async function switchMap(def: MapDef): Promise<void> {
  loading = true;
  currentDef = def;
  engine.setBloomFloor(def.forceBloom === true); // glowy maps bloom even on low
  // Free outgoing map geometry. Materials/HDRI are shared caches — keep them.
  engine.scene.traverse((obj) => {
    if (obj instanceof Mesh) {
      obj.geometry.dispose();
    }
  });
  engine.scene.clear();
  await addEnvironment(engine.scene, def.palette);
  level = await def.load(engine.scene);
  collider = new LevelCollider(level.colliders);
  player = new PlayerController(collider, level.spawn, level.killY ?? -2000);
  timer = new RunTimer(level.zones);
  triggers = new MapTriggers(level.zones);
  abilityWorld.clear(); // drop any portals/causeway from the previous map
  abilityWorld.setPlayer(player);
  // Combat: fresh sim per map. The player always registers (health HUD);
  // dummies only exist where the map declares them (spec 0026).
  combat.clear();
  dummies.clear();
  combat.setCollider(collider);
  combat.register({
    id: 'player',
    team: 'blue',
    maxHealth: PLAYER_MAX_HEALTH,
    radius: PLAYER_RADIUS,
    height: PLAYER_HEIGHT,
    getPosition: () => player.position,
    respawnSec: 0, // player death is handled by resetRun, not auto-respawn
  });
  dummies.load(level.combat?.dummies ?? [], collider, level.killY ?? -2000);
  duelist?.clear();
  duelist = null;
  duelKills = 0;
  duelDeaths = 0;
  const duelistSpec = level.combat?.duelist;
  if (duelistSpec) {
    const hero = resolveCharacter(duelistSpec.heroId);
    duelist = new DuelistChampion(engine.scene, combat, duelistSpec, {
      collider,
      killY: level.killY ?? -2000,
      tuning: { ...resolveCharacterTuning(hero) }, // own copy (spec 0027)
      abilities: hero.abilities ?? [],
      castRange: { 'thunder-stomp': SEISMIC_SLAM_RADIUS * 0.85 },
      accent: accentColor(hero.accent),
      world: abilityWorld,
      targetPos: () => player.position,
    });
  }
  projectileVisuals.reattach(); // scene.clear() above dropped the pooled meshes
  // Ghost replay: load this map's saved PB path; scene.clear() above dropped the
  // mesh, so re-add it (it's reused across maps) and start a fresh recording.
  recorder.reset();
  ghost = loadGhost(def.id);
  ghostMesh.visible = false;
  engine.scene.add(ghostMesh);
  slowmo.reset();
  engine.setTimeScale(1);
  look.yaw = level.spawnYaw;
  look.pitch = -0.1;
  splitMessage = '';
  finishHandled = false;
  hud.setBanner(null);
  sync.reset();
  passiveMeter.reset();
  surgeFlashSec = 0;
  try {
    localStorage.setItem(LAST_MAP_KEY, def.id);
  } catch {
    // Storage unavailable — last map just isn't remembered.
  }
  for (const button of mapList?.querySelectorAll('button') ?? []) {
    button.classList.toggle('active', button.dataset.mapId === def.id);
  }
  loading = false;
}

for (const def of MAPS) {
  const button = document.createElement('button');
  const label = document.createElement('span');
  label.textContent = def.name;
  const meta = document.createElement('span');
  meta.className = 'map-meta';
  const tier = document.createElement('span');
  tier.className = 'map-tier';
  tier.textContent = def.tier ? `T${def.tier}` : 'Free surf';
  const badge = document.createElement('span');
  badge.className = `map-badge ${medalBadgeClass(records.maps[def.id]?.pbTicks, def)}`;
  meta.append(tier, badge);
  button.append(label, meta);
  button.dataset.mapId = def.id;
  button.addEventListener('click', (e) => {
    e.stopPropagation(); // don't trigger the overlay's pointer-lock click
    audio.start(); // user gesture — safe place to create the AudioContext
    audio.click();
    if (!loading && def.id !== level.mapId) {
      void switchMap(def);
    }
  });
  mapList.appendChild(button);
}

// One-time pivot landing (ADR 0007): returning players carry a practice map in
// LAST_MAP_KEY and would resume the old game without ever seeing the surf-MOBA.
// The first load after the pivot lands on the Proving Grounds instead; after
// that, normal last-map behavior resumes. An explicit ?map= always wins.
const PIVOT_SEEN_KEY = 'fall-line.pivotSeen.v1';
const initialId =
  new URLSearchParams(window.location.search).get('map') ??
  (() => {
    try {
      if (!localStorage.getItem(PIVOT_SEEN_KEY)) {
        localStorage.setItem(PIVOT_SEEN_KEY, '1');
        return 'arena_proving_grounds';
      }
      return localStorage.getItem(LAST_MAP_KEY);
    } catch {
      return null;
    }
  })();
await switchMap(resolveMap(initialId));

installPageHook({
  switchMap: (id) => switchMap(resolveMap(id)),
  getLevel: () => level,
  getPlayer: () => player,
  getTimer: () => timer,
  engine,
  look,
  setBotActive: (b) => {
    botActive = b;
  },
  castAbility: (id) => {
    const ability = (currentCharacter.abilities ?? []).find((a) => a.id === id);
    ability?.activate?.({ player, yaw: look.yaw, pitch: look.pitch, world: abilityWorld, combat });
  },
  getCombatState: () =>
    [
      'player',
      ...(level.combat?.dummies ?? []).map((d) => d.id),
      ...(level.combat?.duelist ? [level.combat.duelist.id] : []),
    ].flatMap((id) => {
      const state = combat.state(id);
      return state ? [{ id, health: state.health, alive: state.alive }] : [];
    }),
  selectHero: (id) => applyCharacter(resolveCharacter(id)),
  teleportPlayer: (pos) => player.teleport(new Vector3(pos[0], pos[1], pos[2])),
});

// --- Input / overlay wiring --------------------------------------------------

let noclip = false;
let prevSpace = false;
const toggles = { KeyN: false, KeyT: false, KeyF: false };
const eye = new Vector3();

const pressedOnce = (code: keyof typeof toggles): boolean => {
  const down = input.pressed(code);
  const fresh = down && !toggles[code];
  toggles[code] = down;
  return fresh;
};

// Generic rising-edge detector for arbitrary key codes (ability binds Q/E/R).
const edgeState = new Map<string, boolean>();
const pressedEdge = (code: string): boolean => {
  const down = input.pressed(code);
  const fresh = down && !edgeState.get(code);
  edgeState.set(code, down);
  return fresh;
};

function syncOverlay(): void {
  overlay?.classList.toggle('hidden', input.isActive || tuningPanel.visible);
}

overlay.addEventListener('click', () => {
  if (loading) {
    return;
  }
  // Ghost-click guard: a tap on the ☰ menu button can synthesize a click that
  // lands here right after the session ended — don't instantly restart it.
  if (touchMode && performance.now() - touchSessionEndedAt < 400) {
    return;
  }
  audio.start(); // user gesture — also the iOS WebAudio unlock
  if (touchMode) {
    // No pointer lock on iOS — a touch session is the play state (spec 0029).
    input.setTouchSession(true);
    touchControls?.show();
    syncOverlay();
  } else {
    input.requestLock();
  }
});
document.addEventListener('pointerlockchange', syncOverlay);
if (touchMode) {
  // Backgrounding (home swipe, tab switch) ends the session so returning
  // users land on the menu instead of a running sim.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      endTouchSession();
    }
  });
}
window.addEventListener('keydown', (e) => {
  // Panel toggle works without pointer lock so its inputs are clickable.
  if (e.code === 'KeyT' && !input.isLocked) {
    tuningPanel.toggle();
    overlay.classList.toggle('hidden', input.isLocked || tuningPanel.visible);
  }
  if (e.code === 'F3') {
    e.preventDefault();
    debugHud.toggle();
  }
});

// Restart the current run: respawn, clear timer/abilities and the banner.
// Shared by the R key and the in-loop restart.
function resetRun(): void {
  player.respawn();
  sync.reset();
  timer.reset();
  triggers.reset();
  abilities.reset();
  combat.resetHealth('player'); // dummies persist through R — they're the map, not the run
  passiveMeter.reset();
  surgeFlashSec = 0;
  clearActiveEffect();
  abilityWorld.clear();
  recorder.reset();
  ghostMesh.visible = false;
  finishHandled = false;
  splitMessage = '';
  hud.setBanner(null);
}

// --- Game loop ----------------------------------------------------------------

engine.onUpdate((dt) => {
  if (botActive) return;
  if (loading || !input.isActive) {
    return;
  }
  if (pressedOnce('KeyT')) {
    tuningPanel.toggle();
  }
  if (pressedOnce('KeyN')) {
    noclip = !noclip;
    if (noclip) {
      fly.position.copy(player.position).y += EYE_HEIGHT;
      fly.velocity.copy(player.velocity);
    } else {
      player.teleport(eye.copy(fly.position).setY(fly.position.y - EYE_HEIGHT));
    }
  }

  if (noclip) {
    fly.update(dt);
    return;
  }

  // Slow-motion trainer: F drops the sim to half speed for a few seconds, then
  // recharges. The Engine applies slowmo.scale in onRender (real-time driven).
  if (pressedOnce('KeyF')) {
    slowmo.toggle();
  }

  look.update();
  if (input.pressed('KeyR')) {
    resetRun();
  }

  // Hero abilities: fire on the rising edge of the bind when off cooldown. The
  // instant part runs before the movement tick (so a blink resolves this frame);
  // a timed effect layers tuning overrides until it expires, then reverts.
  for (const ability of currentCharacter.abilities ?? []) {
    if (pressedEdge(ability.key) && abilities.ready(ability.id)) {
      const result = ability.activate?.({
        player,
        yaw: look.yaw,
        pitch: look.pitch,
        world: abilityWorld,
        combat,
      });
      if (result === false) {
        continue; // mid-sequence (e.g. causeway first click) — no cooldown yet
      }
      if (ability.effect) {
        Object.assign(tuning, currentBaseTuning, ability.effect.tuning);
        effects.trigger(ability.id, ability.effect.durationSec);
        activeEffectId = ability.id;
      }
      abilities.trigger(ability.id, ability.cooldown);
    }
  }
  abilities.tick(dt);
  effects.tick(dt);
  if (activeEffectId && effects.ready(activeEffectId)) {
    Object.assign(tuning, currentBaseTuning); // buff expired — revert to hero feel
    activeEffectId = null;
  }
  // Auto-bhop off → jump only on the rising edge of Space (re-press per hop);
  // on → hop while held (the controller jumps on the first grounded tick).
  const spaceDown = input.pressed('Space');
  const jump = settings.autoBhop ? spaceDown : spaceDown && !prevSpace;
  prevSpace = spaceDown;
  // A jump only fires when grounded at tick start; play the whoosh then.
  if (jump && player.grounded) {
    audio.jump();
  }
  const speedBefore = player.horizontalSpeed();
  const splitsBefore = timer.splits.length;
  const airborneBefore = !player.grounded;
  const fallSpeedBefore = Math.max(0, -player.velocity.y);
  player.tick(
    {
      forward: input.axis('KeyW', 'KeyS'),
      right: input.axis('KeyD', 'KeyA'),
      jump,
    },
    look.yaw,
    dt,
  );
  sync.record(!player.grounded, look.lastYawDelta, player.horizontalSpeed() - speedBefore);
  if (airborneBefore && player.grounded) {
    audio.land(fallSpeedBefore);
  }
  // Passive resource: charge from this tick's playstyle; a full meter surges —
  // running cooldowns are hastened and the meter/VFX flash in the hero accent.
  surgeFlashSec = Math.max(0, surgeFlashSec - dt);
  const passive = currentCharacter.passive;
  if (passive) {
    const justLanded = airborneBefore && player.grounded;
    const surged = passiveMeter.tick(passive, {
      dt,
      grounded: player.grounded,
      surfing: player.surfing,
      justLanded,
      landingFallSpeed: justLanded ? fallSpeedBefore : 0,
      horizontalSpeed: player.horizontalSpeed(),
      strafeGain:
        !player.grounded &&
        Math.abs(look.lastYawDelta) > 1e-4 &&
        player.horizontalSpeed() > speedBefore,
    });
    if (surged) {
      abilities.hasten(passive.surgeHasteSec);
      surgeFlashSec = 0.5;
      abilityWorld.spawnSparkleBurst(player.position, accentColor(currentCharacter.accent), {
        count: 50,
        life: 0.5,
        radius: 60,
      });
    }
  }
  timer.tick(player.position);
  if (timer.state === 'running') {
    recorder.record(timer.ticks, player.position); // capture the live run for a future ghost
  }
  triggers.tick(player);
  abilityWorld.tick(dt); // portal pass-through + causeway expiry
  // Combat sim: patrol dummies + projectiles/respawns, then route this tick's
  // damage events to presentation (numbers, bursts) — the single sim→render seam.
  dummies.tick(dt);
  duelist?.tick(dt);
  combat.tick(dt);
  for (const ev of combat.drainEvents()) {
    damageNumbers.spawn(ev.position, ev.amount, { lethal: ev.lethal });
    abilityWorld.spawnSparkleBurst(ev.position, ev.lethal ? 0xff5544 : 0xffd45e, {
      count: ev.lethal ? 120 : 30,
      life: ev.lethal ? 0.5 : 0.35,
      radius: ev.lethal ? 90 : 30,
    });
    if (ev.lethal && ev.targetId === 'player') {
      duelDeaths++;
      resetRun(); // death is information: instant restart, score remembers
    } else if (ev.lethal && ev.targetId === level.combat?.duelist?.id) {
      duelKills++;
    }
  }

  const split = timer.splits[timer.splits.length - 1];
  if (timer.splits.length > splitsBefore && split) {
    const pbSplit = records.maps[level.mapId]?.splits[split.checkpoint];
    const delta =
      pbSplit === undefined
        ? ''
        : ` (${split.ticks <= pbSplit ? '-' : '+'}${formatTicks(Math.abs(split.ticks - pbSplit))})`;
    splitMessage = `CP${split.checkpoint + 1} ${formatTicks(split.ticks)}${delta}`;
    splitMessageTicks = 200; // ~2s
  }
  if (splitMessageTicks > 0) {
    splitMessageTicks--;
    if (splitMessageTicks === 0) {
      splitMessage = '';
    }
  }

  if (timer.state === 'finished' && !finishHandled) {
    finishHandled = true;
    const previous = records.maps[level.mapId];
    const summary = buildRunSummary(
      timer.ticks,
      timer.splits,
      timer.valid,
      previous ? { ...previous, splits: [...previous.splits] } : undefined,
      currentDef.medals,
    );
    if (timer.valid) {
      const isPb = !previous || timer.ticks < previous.pbTicks;
      submitRun(
        records,
        level.mapId,
        timer.ticks,
        timer.splits.map((s) => s.ticks),
      );
      refreshMapBadges();
      if (isPb) {
        // New PB → this run becomes the ghost to race next time (and on restart).
        saveGhost(recorder.build(level.mapId, timer.ticks));
        ghost = loadGhost(level.mapId);
      }
    }
    hud.setBanner(summary);
    if (timer.valid) {
      audio.finish();
    }
  }
});

engine.onRender((alpha, frameDt) => {
  if (loading) {
    return;
  }
  level.update?.(frameDt); // animated decor (rune gates, portal) — real-time
  // High-speed feel: ease a FOV kick + vignette in above ~900 u/s. Paused,
  // noclip, or reduced-motion frames decay to the plain view.
  const rushSpeed =
    input.isActive && !noclip && !reducedMotion.matches ? player.horizontalSpeed() : 0;
  speedFeel.update(rushSpeed, frameDt);
  const targetFov = effectiveFov(settings.fov + speedFeel.fovKick(), engine.camera.aspect);
  if (Math.abs(engine.camera.fov - targetFov) > 0.01) {
    engine.camera.fov = targetFov;
    engine.camera.updateProjectionMatrix();
  }
  engine.setSpeedVignette(speedFeel.vignetteBoost());
  // Screen-edge glow during an ultimate, tinted with its accent (effect.glow).
  const glow = activeEffectId
    ? (currentCharacter.abilities ?? []).find((a) => a.id === activeEffectId)?.effect?.glow
    : undefined;
  if (glow !== undefined) {
    const r = (glow >> 16) & 0xff;
    const g = (glow >> 8) & 0xff;
    const b = glow & 0xff;
    apexOverlay.style.setProperty('--glow-rgb', `${r},${g},${b}`);
    apexOverlay.style.opacity = String(0.55 + 0.35 * Math.sin(performance.now() / 110));
  } else {
    apexOverlay.style.opacity = '0';
  }
  if (noclip || !input.isActive) {
    // Paused or flying: run at real time; freeze the slow-mo timer.
    engine.setTimeScale(1);
  } else {
    slowmo.tick(frameDt); // real-time driven, so the window lasts a fixed wall-clock
    engine.setTimeScale(slowmo.scale);
  }
  // Combat presentation: projectile mirror, dummy health bars, damage numbers,
  // and the player health bar (shown only on maps with combat content).
  projectileVisuals.update();
  dummies.updateBars();
  duelist?.updateBar();
  damageNumbers.update(engine.camera, frameDt);
  const playerCombat = level.combat ? combat.state('player') : undefined;
  hud.setHealth(playerCombat ? { health: playerCombat.health, max: PLAYER_MAX_HEALTH } : null);
  hud.setScore(level.combat?.duelist ? { kills: duelKills, deaths: duelDeaths } : null);
  if (noclip) {
    engine.camera.position.copy(fly.position);
    hud.update(fly.velocity.length());
  } else {
    eye.lerpVectors(player.prevPosition, player.position, alpha);
    eye.y += EYE_HEIGHT;
    engine.camera.position.copy(eye);
    // Race the PB ghost: sample its path at the live (sub-tick) run time.
    if (ghost && timer.state === 'running' && sampleReplay(ghost, timer.ticks + alpha, ghostPos)) {
      ghostMesh.position.copy(ghostPos);
      ghostMesh.position.y += 36; // lift the orb to roughly chest height
      ghostMesh.visible = true;
    } else {
      ghostMesh.visible = false;
    }
    const timerLine =
      timer.state === 'idle'
        ? ''
        : `${formatTicks(timer.ticks)}${timer.valid ? '' : ' !'}${splitMessage ? `  ${splitMessage}` : ''}`;
    hud.update(player.horizontalSpeed(), timerLine, input.isLocked ? slowmo.hudLine() : '');
    hud.setAbilities(
      (currentCharacter.abilities ?? []).map((a) => ({
        id: a.id,
        key: a.key,
        name: a.name,
        icon: a.icon,
        accent: currentCharacter.accent,
        remaining: abilities.remaining(a.id),
        cooldown: a.cooldown,
        active: activeEffectId === a.id || abilityWorld.isActive(a.id),
      })),
    );
    hud.setResource(
      currentCharacter.passive
        ? {
            name: currentCharacter.passive.name,
            fraction: passiveMeter.fraction,
            accent: currentCharacter.accent,
            surging: surgeFlashSec > 0,
          }
        : null,
    );
  }
  engine.camera.rotation.set(look.pitch, look.yaw, 0, 'YXZ');
  debugHud.update(player, sync);
});

engine.start();
